import {
  faArrowDownWideShort as NewestFirstIcon,
  faArrowUpWideShort as OldestFirstIcon,
  faChevronUp as ViewLessIcon,
  faChevronDown as ViewMoreIcon
} from '@fortawesome/pro-regular-svg-icons';
import { faCommentLines as MessageIcon } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import classNames from 'classnames';
import { useAtom } from 'jotai';
import { orderBy } from 'lodash';
import { useMemo } from 'react';

import { ticketMessagesAtom } from '@/pages/Ticket';
import { isLayoutMobileAtom } from '@/store/layout';

import { Message } from '../Message';
import {
  areMessagesAscendingAtom,
  areMessagesExpandedAtom
} from './Messages.store';
import useStyles from './Messages.styles';

const Messages = () => {
  const [areMessagesAscending, setAreMessagesAscending] = useAtom(
    areMessagesAscendingAtom
  );
  const [areMessagesExpanded, setAreMessagesExpanded] = useAtom(
    areMessagesExpandedAtom
  );
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [conversation] = useAtom(ticketMessagesAtom);
  const { classes } = useStyles();

  const messageList = conversation || [];
  const visibleMessages =
    areMessagesExpanded || !isLayoutMobile ? messageList.length : 4;
  const sortedMessageList = useMemo(() => {
    if (areMessagesAscending) {
      return orderBy(messageList, ['created_at'], ['asc']);
    }
    return orderBy(messageList, ['created_at'], ['desc']);
  }, [areMessagesAscending, messageList]);

  const handleMessageSort = () => {
    setAreMessagesAscending(!areMessagesAscending);
  };

  const handleViewMore = () => {
    setAreMessagesExpanded(!areMessagesExpanded);
  };

  return (
    <div style={{ position: 'relative' }}>
      {conversation.length !== 0 && (
        <>
          <List sx={{ ml: -1, mt: 2 }}>
            <Stack
              direction="row"
              sx={{
                mb: 2,
                width: '100%',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Typography component="h3" variant="h4" sx={{ ml: 1 }}>
                Messages
              </Typography>
              {sortedMessageList.length > 1 && (
                <Button
                  color="primary"
                  variant="outlined"
                  startIcon={
                    areMessagesAscending ? (
                      <FontAwesomeIcon icon={OldestFirstIcon} size="xs" />
                    ) : (
                      <FontAwesomeIcon icon={NewestFirstIcon} size="xs" />
                    )
                  }
                  sx={{ whiteSpace: 'nowrap' }}
                  onClick={handleMessageSort}
                >
                  {areMessagesAscending ? 'Oldest First' : 'Newest First'}
                </Button>
              )}
            </Stack>
            {sortedMessageList.length > 0 &&
              sortedMessageList
                .slice(0, visibleMessages)
                .map((message) => (
                  <Message key={message.id} message={message} />
                ))}
            {messageList.length > 4 && isLayoutMobile && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  className={classes.messagesViewButton}
                  sx={{ color: 'primary.main' }}
                  onClick={handleViewMore}
                >
                  {areMessagesExpanded && (
                    <div
                      className={classNames(
                        'viewLessButton',
                        classes.messagesViewButtonText
                      )}
                    >
                      View Less{' '}
                      <FontAwesomeIcon icon={ViewLessIcon} size="lg" />
                    </div>
                  )}
                  {!areMessagesExpanded && (
                    <div
                      className={classNames(
                        'viewMoreButton',
                        classes.messagesViewButtonText
                      )}
                    >
                      View More{' '}
                      <FontAwesomeIcon icon={ViewMoreIcon} size="lg" />
                    </div>
                  )}
                </Typography>
              </Box>
            )}
          </List>
          {messageList.length > 4 && isLayoutMobile && !areMessagesExpanded && (
            <div className={classes.messagesOverlay} />
          )}
        </>
      )}
      {conversation.length === 0 && (
        <Grid container>
          <Stack
            direction="column"
            sx={{
              mt: 3,
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FontAwesomeIcon icon={MessageIcon} size="2xl" />
            <Typography variant="h5" gutterBottom>
              No messages yet!
            </Typography>
            <Typography variant="body1" gutterBottom>
              Be the first to say something by typing a message above.
            </Typography>
          </Stack>
        </Grid>
      )}
    </div>
  );
};

export default Messages;
